import { siteApiSlice } from "../../app/siteApiSlice";
import { UserRole } from "../../app/userSlice";
import { DataTablesViewModel, DataTablesQueryModel } from "../../interfaces/dataTables";
import { WritingAction } from "../writing/writingApiSlice";

export enum NotificationTypes {
	LessonAssigned = "Lesson Assignment",
	DraftComment = "Feedback",
	DraftFinalize = "Essay Finalized",
	DraftSubmit = "Writing",
	PeerReviewRequest = "Peer Review Request",
	PeerReview = "Peer Review",
	NewMessage = "Message",
	DueDate = "Due Date",
	DueDateExceeded = "Due Date",
}

export interface NotificationDataTableQueryModel extends DataTablesQueryModel {
	typeFilter: string;
}

export interface NotificationDataTableViewModel
	extends DataTablesViewModel<NotificationBaseViewModel> {
	notificationTypes: string;
}

export interface NotificationBaseViewModel {
	title: string;
	body: string;
	href: string;
	avatarUserId: number;
	avatarUserHasImage: boolean;
	avatarUserInitials: string;
	forceShowWithoutInitials: boolean;
	viewed: boolean;
	receivedDate: string;
	notificationType: string;
	id: number;
	canBeSetAsViewed: boolean;
	canBeDeleted: boolean;
	lessonCodeName?: string;
	lessonName?: string;
	fromUser?: number;
	fromUserRole?: UserRole;
	lastDraftId?: number;
	promptName?: string;
	draftPeerReviewId?: number;
	isAnonymous?: boolean;
	fromUtcDate?: string;
	writerName?: string;
	draftId?: number;
	promptId?: number;
}

export interface LastNotificationsViewModel {
	status: string;
	notificationModels: NotificationBaseViewModel[];
}

export interface UnreadCount {
	count: number;
}

export const notificationApiSlice = siteApiSlice.injectEndpoints({
	endpoints: (build) => ({
		notifications: build.query<NotificationDataTableViewModel, NotificationDataTableQueryModel>(
			{
				query: (q) => {
					const { iSortCol_0, sSortDir_0, iDisplayStart, iDisplayLength, typeFilter } = q;
					return {
						url: `/Notification/NotificationsAjaxHandler`,
						params: {
							iSortCol_0,
							sSortDir_0,
							iDisplayStart,
							iDisplayLength,
							typeFilter,
						},
					};
				},
				providesTags: ["CurrentCourse", "Notifications"],
			}
		),
		lastNotifications: build.query<LastNotificationsViewModel, void>({
			query: (payload) => ({
				url: `/Notification/GetLastNotifications`,
				method: "POST",
				body: payload,
			}),
			providesTags: ["CurrentCourse", "Notifications"],
		}),
		unreadNotificationCount: build.query<UnreadCount, void>({
			query: () => ({
				url: `/Notification/UnreadNotificationCountAjax`,
			}),
			providesTags: ["CurrentCourse", "Notifications"],
		}),
		draftAction: build.query<{ action: WritingAction }, number>({
			query: (draftId) => ({
				url: `/Draft/GetDraftAction?draftId=${draftId}`,
			}),
		}),
		setNotificationsAsViewed: build.mutation<void, number[]>({
			query: (rowIds) => ({
				url: `/Notification/SetNotificationsAsViewed`,
				method: "POST",
				body: { notificationIds: rowIds },
			}),
			invalidatesTags: ["Notifications"],
		}),
		deleteNotifications: build.mutation<void, number[]>({
			query: (rowIds) => ({
				url: `/Notification/DeleteNotifications`,
				method: "POST",
				body: { notificationIds: rowIds },
			}),
			invalidatesTags: ["Notifications"],
		}),
	}),
});

export const {
	useNotificationsQuery,
	useLastNotificationsQuery,
	useUnreadNotificationCountQuery,
	useDraftActionQuery,
	useLazyDraftActionQuery,
	useSetNotificationsAsViewedMutation,
	useDeleteNotificationsMutation,
} = notificationApiSlice;
